import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../services/API";

export const createDraft = createAsyncThunk("createDraft", async (data, { dispatch }) => {
    const form_data = new FormData();
    form_data.append("s3_key", data.s3_key);
    form_data.append("post_description", data.body);
    form_data.append("is_private", data.is_private ? "True" : "False");
    form_data.append("comments_enabled", data.comments_enabled ? "True" : "False");
    if (data.download_enabled !== undefined) {
        form_data.append("download_enabled", data.download_enabled ? "True" : "False");
    }

    const response = await axiosInstance.post("/post/drafts/create/", form_data, {
        headers: {
            "content-type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            dispatch(setUploadProgress(percentCompleted));
        },
    });

    try {
        const result = response.data;
        return result;
    } catch (error) {
        return error;
    }
});

export const updateDraft = createAsyncThunk("updateDraft", async (data) => {
    const form_data = new FormData();
    if (data.video) {
        form_data.append("video", data.video);
    }
    form_data.append("interest", data.interest);
    form_data.append("post_description", data.body);
    form_data.append("is_private", data.is_private ? "True" : "False");
    if (data.comments_enabled !== undefined) {
        form_data.append("comments_enabled", data.comments_enabled ? "True" : "False");
    }
    if (data.download_enabled !== undefined) {
        form_data.append("download_enabled", data.download_enabled ? "True" : "False");
    }

    const response = await axiosInstance.patch(`/post/drafts/${data.id}/update/`, form_data, {
        headers: {
            "content-type": "multipart/form-data",
        },
    });

    try {
        const result = response.data;
        return result;
    } catch (error) {
        return error;
    }
});

export const publishDraft = createAsyncThunk("publishDraft", async (data, { dispatch }) => {
    const form_data = new FormData();
    if (data.video) {
        form_data.append("video", data.video);
    }
    form_data.append("post_description", data.body);
    form_data.append("is_private", data.is_private ? "True" : "False");
    form_data.append("interest", data.interest);
    if (data.comments_enabled !== undefined) {
        form_data.append("comments_enabled", data.comments_enabled ? "True" : "False");
    }
    if (data.download_enabled !== undefined) {
        form_data.append("download_enabled", data.download_enabled ? "True" : "False");
    }

    const response = await axiosInstance.patch(`/post/drafts/${data.id}/publish/`, form_data, {
        headers: {
            "content-type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            dispatch(setUploadProgress(percentCompleted));
        },
    });

    try {
        const result = response.data;
        return result;
    } catch (error) {
        return error;
    }
});

export const deleteDraft = createAsyncThunk("deleteDraft", async (id) => {
    const response = await axiosInstance.delete(`/post/drafts/${id}/delete/`);
    try {
        const result = response.data;
        return result;
    } catch (error) {
        return error;
    }
});

export const getAllDrafts = createAsyncThunk("getAllDrafts", async ({ nextUrl, isLoadMore }) => {
    try {
        let response;
        if (nextUrl) {
            const url = new URL(nextUrl);
            const pathAndQuery = url.pathname + url.search;
            const relativePath = pathAndQuery.replace('/api', '');
            response = await axiosInstance.get(relativePath);
        } else {
            response = await axiosInstance.get(`/post/drafts/`);
        }

        const result = response.data;
        return { ...result, isLoadMore };
    } catch (error) {
        return error;
    }
});

export const getDraftById = createAsyncThunk("getDraftById", async (id) => {
    const response = await axiosInstance.get(`/post/drafts/${id}/`);
    try {
        const result = response.data;
        return result;
    } catch (error) {
        return error;
    }
});

export const DraftSlice = createSlice({
    name: "Draft",
    initialState: {
        draftVideos: { results: { drafts: [] } },
        loading: false,
        loadingMore: false,
        error: null,
        uploadProgress: 0,
        nextPage: null,
    },
    reducers: {
        setUploadProgress: (state, action) => {
            state.uploadProgress = action.payload;
        },
        resetLoadingMore: (state) => {
            state.loadingMore = false;
        },
        resetDraftUploadState: (state) => {
            state.loading = false;
            state.uploadProgress = 0;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(createDraft.pending, (state) => {
            state.loading = true;
            state.uploadProgress = 0;
        });
        builder.addCase(createDraft.fulfilled, (state, action) => {
            if (!state.draftVideos || !state.draftVideos.results || !Array.isArray(state.draftVideos.results.drafts)) {
                state.draftVideos = { results: { drafts: [] } };
            }
            state.draftVideos.results.drafts.push(action.payload);

            if (typeof state.draftVideos.results.total_draft_count === 'number') {
                state.draftVideos.results.total_draft_count++;
            }

            state.loading = false;
            state.uploadProgress = 0;
        });
        builder.addCase(createDraft.rejected, (state, action) => {
            state.error = action.error.message;
            state.loading = false;
            state.uploadProgress = 0;
        });

        builder.addCase(updateDraft.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(updateDraft.fulfilled, (state, action) => {
            if (state.draftVideos && state.draftVideos.results && Array.isArray(state.draftVideos.results.drafts)) {
                const index = state.draftVideos.results.drafts.findIndex((draft) => draft.id === action.payload.id);
                if (index !== -1) {
                    state.draftVideos.results.drafts[index] = action.payload;
                }
            } else if (state.draftVideos && state.draftVideos.id === action.payload.id) {
                state.draftVideos = action.payload;
            }
            state.loading = false;
        });
        builder.addCase(updateDraft.rejected, (state, action) => {
            state.error = action.error.message;
            state.loading = false;
        });

        // Publish Draft action promises
        builder.addCase(publishDraft.pending, (state) => {
            state.loading = true;
            state.uploadProgress = 0;
        });
        builder.addCase(publishDraft.fulfilled, (state, action) => {
            if (state.draftVideos && state.draftVideos.results && Array.isArray(state.draftVideos.results.drafts)) {

                const index = state.draftVideos.results.drafts.findIndex((draft) => draft.id === action.payload.id);
                if (index !== -1) {
                    state.draftVideos.results.drafts.splice(index, 1);


                    if (typeof state.draftVideos.results.total_draft_count === 'number') {
                        state.draftVideos.results.total_draft_count--;
                    }
                }
            } else if (state.draftVideos && state.draftVideos.id === action.payload.id) {
                state.draftVideos = null;
            }
            state.loading = false;
            state.uploadProgress = 0;
        });
        builder.addCase(publishDraft.rejected, (state, action) => {
            state.error = action.error.message;
            state.loading = false;
            state.uploadProgress = 0;
        });

        // Delete Draft action promises
        builder.addCase(deleteDraft.pending, (state) => {
            // state.loading = true;
        });
        builder.addCase(deleteDraft.fulfilled, (state, action) => {
            // Ensure state.draftVideos is properly structured
            if (state.draftVideos && state.draftVideos.results && Array.isArray(state.draftVideos.results.drafts)) {
                // Filter out the deleted video
                state.draftVideos.results.drafts = state.draftVideos.results.drafts.filter(
                    (draft) => draft.id !== action.meta.arg
                );

                // Update the total count
                if (typeof state.draftVideos.results.total_draft_count === 'number') {
                    state.draftVideos.results.total_draft_count--;
                }
            }
            state.loading = false;
        });
        builder.addCase(deleteDraft.rejected, (state, action) => {
            state.error = action.error.message;
            state.loading = false;
        });

        // Get All Drafts action promises
        builder.addCase(getAllDrafts.pending, (state, action) => {
            const { isLoadMore } = action.meta.arg || {};
            if (isLoadMore) {
                state.loadingMore = true;
            } else {
                state.loading = true;
            }
        });
        builder.addCase(getAllDrafts.fulfilled, (state, action) => {
            const { isLoadMore } = action.payload;

            if (isLoadMore) {
                if (action.payload.results && action.payload.results.drafts && Array.isArray(action.payload.results.drafts)) {
                    if (!state.draftVideos || !state.draftVideos.results || !Array.isArray(state.draftVideos.results.drafts)) {
                        state.draftVideos = { results: { drafts: [] } };
                    }
                    state.draftVideos.results.drafts = [
                        ...state.draftVideos.results.drafts,
                        ...action.payload.results.drafts
                    ];
                    if (action.payload.results.total_draft_count !== undefined) {
                        state.draftVideos.results.total_draft_count = action.payload.results.total_draft_count;
                    }
                }
                state.loadingMore = false;
            } else {
                state.draftVideos = action.payload || { results: { drafts: [] } };
                state.loading = false;
            }

            state.nextPage = action.payload?.next || null;
        });
        builder.addCase(getAllDrafts.rejected, (state, action) => {
            const { isLoadMore } = action.meta.arg || {};
            state.error = action.error.message;
            if (isLoadMore) {
                state.loadingMore = false;
            } else {
                state.loading = false;
            }
        });

        // Get Draft By Id action promises
        builder.addCase(getDraftById.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getDraftById.fulfilled, (state, action) => {
            state.draftVideos = action.payload;
            state.loading = false;
        });
        builder.addCase(getDraftById.rejected, (state, action) => {
            state.error = action.error.message;
            state.loading = false;
        });
    },
});

export const { setUploadProgress, resetLoadingMore, resetDraftUploadState } = DraftSlice.actions;
export default DraftSlice.reducer;